/**
 * Campaign inputs (PRD v2 §3). Deliberately small: a name, what it says, who gets it. There
 * is no schedule field and no "send again" — one-shot is the whole product decision.
 */
import { z } from 'zod';
import { idempotency } from './spine';
import { CAMPAIGN_AUDIENCES } from '../campaigns';

export const campaignAudience = z.enum(CAMPAIGN_AUDIENCES);

const audienceFields = {
  audience: campaignAudience,
  /** Months for recent/lapsed; the lead-source string for lead_source. Ignored by `all`. */
  audienceParam: z.string().trim().max(80).optional().or(z.literal('')),
};

export const campaignCreateInput = idempotency.extend({
  name: z.string().trim().min(1, 'Give it a name so you can find it later').max(120),
  subject: z.string().trim().max(200).optional().or(z.literal('')),
  body: z.string().max(20_000).optional().or(z.literal('')),
  ...audienceFields,
});
export type CampaignCreateInput = z.infer<typeof campaignCreateInput>;

export const campaignUpdateInput = idempotency.extend({
  campaignId: z.string().uuid(),
  name: z.string().trim().min(1).max(120).optional(),
  subject: z.string().trim().max(200).optional().or(z.literal('')),
  body: z.string().max(20_000).optional().or(z.literal('')),
  audience: campaignAudience.optional(),
  audienceParam: z.string().trim().max(80).optional().or(z.literal('')),
});
export type CampaignUpdateInput = z.infer<typeof campaignUpdateInput>;

export const campaignIdInput = z.object({ campaignId: z.string().uuid() });
export type CampaignIdInput = z.infer<typeof campaignIdInput>;

export const campaignDeleteInput = idempotency.extend({
  campaignId: z.string().uuid(),
});
export type CampaignDeleteInput = z.infer<typeof campaignDeleteInput>;

/**
 * Sending. `confirm` must be the literal true — there is no undo on a broadcast, and the
 * one-shot rule means a mistake can't be corrected by sending a fixed version to the same
 * list without the whole list getting it twice.
 */
export const campaignSendInput = idempotency.extend({
  campaignId: z.string().uuid(),
  confirm: z.literal(true),
});
export type CampaignSendInput = z.infer<typeof campaignSendInput>;

/** The public opt-out. No auth: requiring a login to unsubscribe is itself a violation. */
export const unsubscribeInput = z.object({
  token: z.string().min(20).max(120),
});
export type UnsubscribeInput = z.infer<typeof unsubscribeInput>;
